import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import { API_URL } from './env';
import { session, getNamespace } from './session';

/** Refresh endpoint per namespace. */
const REFRESH_PATH: Record<string, string> = {
  admin: '/v1/merchant/auth/refresh',
  backoffice: '/v1/backoffice/auth/refresh',
};

export const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

/** Attach the bearer token to every request. */
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = session.getToken();
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshing: Promise<string | null> | null = null;

async function runRefresh(): Promise<string | null> {
  const namespace = getNamespace();
  const refreshToken = session.getRefreshToken();
  if (!refreshToken) return null;

  try {
    const { data } = await axios.post<{ accessToken: string; refreshToken: string; user: unknown }>(
      `${API_URL}${REFRESH_PATH[namespace]}`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );
    const currentUser = session.getUser();
    session.set(data.accessToken, data.refreshToken, data.user ?? currentUser);
    return data.accessToken;
  } catch {
    session.clear();
    return null;
  }
}

/** On 401, try a single refresh + retry, then give up (and clear the session). */
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;

    if (error.response?.status === 401 && original && !original._retried) {
      original._retried = true;
      refreshing = refreshing ?? runRefresh();
      const newToken = await refreshing;
      refreshing = null;

      if (newToken) {
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
        return api(original);
      }
    }

    return Promise.reject(error);
  }
);

/** Standard error body returned by the core (`ErrorResponse`). */
interface ApiErrorBody {
  message?: string;
  errorCode?: string;
  details?: { validationErrors?: Record<string, string> };
}

/** Business error code returned by the core (e.g. `CATEGORY_IN_USE`). */
export function extractErrorCode(error: unknown): string | undefined {
  return (error as AxiosError<ApiErrorBody>)?.response?.data?.errorCode;
}

/**
 * Extract a human-readable message from an axios error.
 *
 * Resolution order: a friendly message mapped from the business `errorCode`,
 * then the field-level validation errors, then the message sent by the core,
 * then the caller's fallback.
 */
export function extractErrorMessage(
  error: unknown,
  fallback: string,
  messagesByCode?: Record<string, string>
): string {
  const body = (error as AxiosError<ApiErrorBody>)?.response?.data;
  const code = body?.errorCode;

  if (code && messagesByCode?.[code]) return messagesByCode[code];

  const validationErrors = body?.details?.validationErrors;
  if (validationErrors) {
    const messages = Object.values(validationErrors).filter(Boolean);
    if (messages.length > 0) return messages.join(' ');
  }

  return body?.message || fallback;
}

export default api;
